import {
  campaignStepDependencies,
  type CampaignReadinessAction,
  type CampaignReadinessCheckId,
  type CampaignReadinessItem,
} from './campaignReadiness';

export const PURSUIT_CRM_SPEC_URL =
  'https://winanscreek.atlassian.net/wiki/spaces/NC/pages/116555797/Pursuit+CRM+product+spec+2026-09-05';

export const CAMPAIGN_PURPOSE_FIELD_ID = 'pursuit-campaign-purpose';

export const campaignPurposeCopy = (spanish: boolean) => ({
  label: spanish ? 'Para qué es esta campaña' : 'What this campaign is for',
  helpLabel: spanish ? 'Acerca de la descripción' : 'About the campaign description',
  help: spanish
    ? 'Un recordatorio de por qué existe este libro. Es distinto de a quién vende (ICP). Las notas de seguimiento de un lead van en el lead, no aquí.'
    : 'A reminder of why this book exists. Separate from who you sell to (ICP). Follow-up notes on a person stay on the lead, not here.',
  placeholder: spanish
    ? 'Para qué es esta campaña. Ejemplo: solicitudes de demo del sitio público.'
    : 'What this campaign is for. Example: inbound demo requests from nextcompli.com.',
  nextStep: spanish ? 'Escribir para qué es esta campaña' : 'Fill in what this campaign is for',
});

export const CAMPAIGN_LAUNCH_GUIDE_HASH: Record<CampaignReadinessCheckId, string> = {
  sku: 'campaign-guide-sku',
  icp: 'campaign-guide-icp',
  notes: 'campaign-guide-notes',
  active: 'campaign-guide-active',
  lead: 'campaign-guide-lead',
  deal: 'campaign-guide-deal',
  sequence: 'campaign-guide-sequence',
  task: 'campaign-guide-task',
  website: 'campaign-guide-website',
  quote: 'campaign-guide-quote',
};

export type CampaignGuideDocLink = {
  label: string;
  href: string;
  external?: boolean;
};

export type CampaignNextStepAction = {
  label: string;
  action: CampaignReadinessAction;
  primary?: boolean;
};

export type CampaignNextStepCopy = {
  title: string;
  where: string;
  how: string;
  depends: string;
  actions: CampaignNextStepAction[];
  docs: CampaignGuideDocLink[];
};

export type CampaignNextStepContext = {
  spanish: boolean;
  productName: string;
  inbound: boolean;
  items: CampaignReadinessItem[];
};

export const campaignStepTitle = (id: CampaignReadinessCheckId, spanish: boolean): string => {
  if (id === 'sku') return spanish ? 'Asociar un SKU' : 'Associate a plan SKU';
  if (id === 'icp') return spanish ? 'Describir el ICP' : 'Describe the ICP';
  if (id === 'notes') return spanish ? 'Añadir por qué existe este movimiento' : 'Add why this motion exists';
  if (id === 'active') return spanish ? 'Poner el estado en Activa' : 'Set status to Active';
  if (id === 'lead') return spanish ? 'Crear un lead con este plan' : 'Create a lead with this plan';
  if (id === 'deal') return spanish ? 'Convertir un lead al pipeline' : 'Convert a lead onto the pipeline';
  if (id === 'sequence') return spanish ? 'Inscribir leads en una secuencia' : 'Enroll leads in a sequence';
  if (id === 'task') return spanish ? 'Crear la primera tarea' : 'Create the first task';
  if (id === 'quote') return spanish ? 'Crear la primera cotización' : 'Create the first quote';
  if (id === 'website') return spanish ? 'Revisar el ingest del sitio' : 'Check website ingest';
  return spanish ? 'Convertir un lead al pipeline' : 'Convert a lead onto the pipeline';
};

const whereLabel = (action: CampaignReadinessAction, spanish: boolean): string => {
  if (action === 'settings') return spanish ? 'Ajustes de campaña' : 'Campaign settings';
  if (action === 'purpose') return spanish ? 'Campañas' : 'Campaigns';
  if (action === 'leads' || action === 'generate') return spanish ? 'Leads' : 'Leads';
  if (action === 'sequences') return spanish ? 'Secuencias' : 'Sequences';
  if (action === 'tasks') return spanish ? 'Tareas' : 'Tasks';
  if (action === 'quotes') return spanish ? 'Cotizaciones' : 'Quotes';
  if (action === 'website') return spanish ? 'Ingest web' : 'Website ingest';
  return spanish ? 'Pipeline' : 'Pipeline';
};

const formatDepends = (id: CampaignReadinessCheckId, ctx: CampaignNextStepContext): string => {
  const deps = campaignStepDependencies(id, ctx.items);
  if (!deps.length) {
    return ctx.spanish ? 'Ningún paso anterior bloquea este.' : 'No earlier step blocks this one.';
  }
  const parts = deps.map((dep) => {
    const title = campaignStepTitle(dep.id, ctx.spanish);
    if (dep.done) return ctx.spanish ? `${title} (listo)` : `${title} (done)`;
    return ctx.spanish ? `${title} (aún abierto)` : `${title} (still open)`;
  });
  return ctx.spanish ? `Depende de: ${parts.join('; ')}.` : `Depends on: ${parts.join('; ')}.`;
};

const guideLink = (id: CampaignReadinessCheckId, spanish: boolean): CampaignGuideDocLink => ({
  label: spanish ? 'Guía de lanzamiento' : 'Campaign launch guide',
  href: `#${CAMPAIGN_LAUNCH_GUIDE_HASH[id]}`,
});

const specLink = (spanish: boolean): CampaignGuideDocLink => ({
  label: spanish ? 'Especificación Pursuit CRM' : 'Pursuit CRM product spec',
  href: PURSUIT_CRM_SPEC_URL,
  external: true,
});

/** One copy of the guide + spec links for the Immediate next steps region — not repeated on every card. */
export const campaignNextStepSectionDocs = (
  firstOpenId: CampaignReadinessCheckId | undefined,
  spanish: boolean,
): CampaignGuideDocLink[] => [guideLink(firstOpenId || 'sku', spanish), specLink(spanish)];

export const campaignNextStepCopy = (
  id: CampaignReadinessCheckId,
  ctx: CampaignNextStepContext,
): CampaignNextStepCopy => {
  const plan = ctx.productName || (ctx.spanish ? 'el SKU de esta campaña' : "this campaign's plan");
  const depends = formatDepends(id, ctx);
  const docs = [guideLink(id, ctx.spanish), specLink(ctx.spanish)];

  if (id === 'sku') {
    return {
      title: campaignStepTitle(id, ctx.spanish),
      where: whereLabel('settings', ctx.spanish),
      how: ctx.spanish
        ? 'Abra Ajustes de campaña. En SKU asociado elija CompliCore, CompliAdvantage o Enterprise. Guarde. Ese SKU es el que deben usar el Plan del lead y el Producto del trato.'
        : 'Open Campaign settings. Under Associated SKU pick CompliCore, CompliAdvantage, or Enterprise. Save. Leads (Plan) and deals (Product) must use that same SKU or this campaign will not count them.',
      depends,
      actions: [
        {
          label: ctx.spanish ? 'Abrir ajustes y elegir SKU' : 'Open settings to pick a SKU',
          action: 'settings',
          primary: true,
        },
      ],
      docs,
    };
  }

  if (id === 'icp') {
    return {
      title: campaignStepTitle(id, ctx.spanish),
      where: whereLabel('leads', ctx.spanish),
      how: ctx.spanish
        ? `Abra Leads. En ICP describa a quién vende, arme la matriz, revísela, y luego busque leads. Dónde buscar es una fase (empresas, industria o competidores), no una frase. Úselo para ${plan}.`
        : `Open Leads. Expand ICP, describe who you sell to, build the matrix from the web, review it, then search for leads. Where to look is a phase (company sites, industry docs, or competitors), not a keyword phrase. Use it for ${plan}.`,
      depends,
      actions: [
        {
          label: ctx.spanish ? 'Abrir Leads para fijar ICP' : 'Open Leads to set ICP',
          action: 'leads',
          primary: true,
        },
      ],
      docs,
    };
  }

  if (id === 'notes') {
    const purpose = campaignPurposeCopy(ctx.spanish);
    return {
      title: campaignStepTitle(id, ctx.spanish),
      where: whereLabel('purpose', ctx.spanish),
      how: ctx.spanish
        ? 'Bajo el título de la campaña, en Para qué es esta campaña, escriba por qué existe este libro. Guarde al salir del campo. El ICP (a quién vende) es otro campo.'
        : 'Under the campaign title, fill in What this campaign is for — why this book exists. It saves when you leave the field. Who you sell to (ICP) is a separate field.',
      depends,
      actions: [
        {
          label: purpose.nextStep,
          action: 'purpose',
          primary: true,
        },
      ],
      docs,
    };
  }

  if (id === 'active') {
    return {
      title: campaignStepTitle(id, ctx.spanish),
      where: whereLabel('settings', ctx.spanish),
      how: ctx.spanish
        ? 'Pulse Poner Activa y guardar. Eso cambia el estado ahora, sin abrir ajustes. Hágalo después del SKU, el ICP y las notas, para que Activa signifique que el movimiento ya puede recibir leads.'
        : 'Click Set Active and save. That changes status now without opening settings. Do this after SKU, ICP, and notes so Active means the motion is ready to take leads.',
      depends,
      actions: [
        {
          label: ctx.spanish ? 'Poner Activa y guardar' : 'Set Active and save',
          action: 'activate',
          primary: true,
        },
        {
          label: ctx.spanish ? 'Abrir ajustes' : 'Open campaign settings',
          action: 'settings',
        },
      ],
      docs,
    };
  }

  if (id === 'lead') {
    return {
      title: campaignStepTitle(id, ctx.spanish),
      where: whereLabel('leads', ctx.spanish),
      how: ctx.inbound
        ? ctx.spanish
          ? 'Los formularios públicos de nextcompli.com escriben leads aquí. Abra Leads para ver solicitudes nuevas, o cree un lead a mano. El canal web no exige SKU en la campaña.'
          : 'Public nextcompli.com forms and the NextCompli Assistant dual-write into this campaign via Inlet. Open Leads to see new demo requests, or add a lead by hand. Website inbound does not require a campaign SKU.'
        : ctx.spanish
          ? `Abra Leads. Describa el ICP, arme la matriz, y pulse Buscar leads para originar empresas con Plan ${plan}. También puede añadir un lead a mano. El Plan debe coincidir con el SKU o el porcentaje no subirá.`
          : `Open Leads. Describe the ICP, build and review the matrix, then Search for leads tagged with Plan ${plan}. Each search appends companies (it does not replace the book). You can still add a lead by hand. The Plan tag must match this campaign's SKU or readiness will not count it.`,
      depends: ctx.inbound
        ? depends
        : `${depends} ${
            ctx.spanish
              ? 'Conviene que la campaña ya esté Activa.'
              : 'Recommended: set the campaign to Active first.'
          }`,
      actions: [
        {
          label: ctx.spanish ? 'Abrir Leads' : 'Open Leads',
          action: 'leads',
          primary: true,
        },
      ],
      docs,
    };
  }

  if (id === 'sequence') {
    return {
      title: campaignStepTitle(id, ctx.spanish),
      where: whereLabel('sequences', ctx.spanish),
      how: ctx.spanish
        ? 'Abra Secuencias. El catálogo ya incluye Discovery follow-up. Inscriba los leads ICP en esa secuencia (o cree otra) para que el seguimiento empiece.'
        : 'Open Sequences. Seed already includes Discovery follow-up. Enroll the ICP leads in that sequence (or create another) so outreach actually starts.',
      depends,
      actions: [
        {
          label: ctx.spanish ? 'Abrir Secuencias' : 'Open Sequences',
          action: 'sequences',
          primary: true,
        },
      ],
      docs,
    };
  }

  if (id === 'task') {
    return {
      title: campaignStepTitle(id, ctx.spanish),
      where: whereLabel('tasks', ctx.spanish),
      how: ctx.spanish
        ? 'Abra Tareas. Cree la siguiente acción (llamar, enviar one-pager, pedir scorecard) con fecha. Eso mantiene el libro en movimiento después del lanzamiento.'
        : 'Open Tasks. Create the next action (call, send a one-pager, ask for the scorecard) with a due date. That keeps the book moving after launch.',
      depends,
      actions: [
        {
          label: ctx.spanish ? 'Abrir Tareas' : 'Open Tasks',
          action: 'tasks',
          primary: true,
        },
      ],
      docs,
    };
  }

  if (id === 'website') {
    return {
      title: campaignStepTitle(id, ctx.spanish),
      where: whereLabel('website', ctx.spanish),
      how: ctx.spanish
        ? 'Abra Ingest web para ver solicitudes públicas dual-write. No sustituye los leads ICP de una campaña saliente; es un segundo canal.'
        : 'Open Website ingest to see public form dual-writes. That does not replace ICP-generated leads on an outbound campaign; it is a second channel.',
      depends,
      actions: [
        {
          label: ctx.spanish ? 'Abrir ingest web' : 'Open website ingest',
          action: 'website',
          primary: true,
        },
      ],
      docs,
    };
  }

  if (id === 'quote') {
    return {
      title: campaignStepTitle(id, ctx.spanish),
      where: whereLabel('quotes', ctx.spanish),
      how: ctx.spanish
        ? 'Abra Cotizaciones. Tras convertir un lead, cree la primera cotización sobre el trato para que el movimiento tenga un precio, no solo una tarjeta en el tablero.'
        : 'Open Quotes. After you convert a lead, create the first quote on that deal so the motion has a price, not only a pipeline card.',
      depends,
      actions: [
        {
          label: ctx.spanish ? 'Abrir Cotizaciones' : 'Open Quotes',
          action: 'quotes',
          primary: true,
        },
      ],
      docs,
    };
  }

  return {
    title: campaignStepTitle(id, ctx.spanish),
    where: whereLabel('pipeline', ctx.spanish),
    how: ctx.spanish
      ? `La conversión está en Leads, no en el tablero. En un lead cuyo Plan sea ${plan}, elija el mismo plan y pulse Convertir. Eso crea cuenta, contacto y trato. Luego abra Pipeline para trabajar la tarjeta.`
      : `Conversion lives on Leads, not the board. On a lead whose Plan is ${plan}, keep that plan and click Convert. That creates the account, contact, and deal. Then open Pipeline to work the card.`,
    depends,
    actions: [
      {
        label: ctx.spanish ? 'Abrir Leads para convertir' : 'Open Leads to convert',
        action: 'leads',
        primary: true,
      },
      {
        label: ctx.spanish ? 'Abrir Pipeline' : 'Open Pipeline',
        action: 'pipeline',
      },
    ],
    docs,
  };
};
